/** A45: секция «Юнифанс» — дословный порт макета (v41): ачивки «N из M»,
 *  карточки игроков (имя+тел+др первой строкой, теги второй, капитан
 *  long-press), «+ данные следующего игрока» с телеграм-кандидатами, медиа,
 *  делегирование блока оператору на филиале. Состояние — у родителя. */
import { useEffect, useState } from "react";
import type { WaiverSaveState } from "./useWaiverSync";
import { api } from "../../api/client";
import { AchPicker, type AchItem } from "./reportFields";
import { TextPad } from "../../components/shared/TextPad";
import { PhonePad } from "../../components/shared/PhonePad";
import { BirthPicker } from "../../components/shared/BirthPicker";
import m from "./opReport.module.css";
import css from "./reports.module.css";

export interface RPlayer { name: string; phone: string; birth_date: string; telegram: string; captain: boolean; tags: string[]; saved?: boolean }
/** Строка таблички «данные с вейверов» в Game review (26.08, владелец). */
/** uf — аккаунт Юнифанса по телефону с сервера: имя / null «нет» / undefined «не проверяли» (13.09) */
export interface WPerson { fio: string; birth_date: string; phone: string; telegram?: string; uf?: string | null; id?: number; lid?: string }   // 03.10: id — строка на сервере, lid — стабильный ключ в форме

/* тумба медиа: превью фото/видео (кадр #t=0.1), длительность, ✕, тап = просмотр */
export function MediaThumb({ f, done, onRemove, onOpen }: { f: File; done: boolean; onRemove: () => void; onOpen: () => void }) {
  const [url] = useState(() => URL.createObjectURL(f));
  const [dur, setDur] = useState<number | null>(null);
  const isImg = f.type.startsWith("image/"), isVid = f.type.startsWith("video/"), isAud = f.type.startsWith("audio/");
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  useEffect(() => {
    if (!isVid && !isAud) return;
    const el = document.createElement(isVid ? "video" : "audio");
    el.preload = "metadata"; el.src = url;
    el.onloadedmetadata = () => { if (Number.isFinite(el.duration)) setDur(el.duration); };
    return () => { el.removeAttribute("src"); el.load?.(); };
  }, [url, isVid, isAud]);
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  return (
    <div className={`${m.th} ${f.size > 19.5 * 1048576 ? m.err : ""}`} title={`${f.name} · ${(f.size / 1048576).toFixed(1)} МБ`}
      onClick={onOpen} role="button" style={{ cursor: "pointer" }}>
      {isImg ? <img className={m.prev} src={url} alt="" />
        : isVid ? <video className={m.prev} src={`${url}#t=0.1`} muted playsInline preload="metadata" />
        : "АУДИО"}
      {dur != null ? <span className={m.dur}>{fmt(dur)}</span> : null}
      {done ? <span className={m.ok2}>✓</span> : null}
      <button type="button" className={m.thx} onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Убрать файл">✕</button>
    </div>
  );
}

/** Полных лет по ISO-дате; -1 если дата битая. */
export const ageOf = (iso: string): number => {
  const mm = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso); if (!mm) return -1;
  const t = new Date(); let a = t.getFullYear() - +mm[1]!;
  if (t.getMonth() + 1 < +mm[2]! || (t.getMonth() + 1 === +mm[2]! && t.getDate() < +mm[3]!)) a--;
  return a;
};

/* лайтбокс просмотра: фото / видео-плеер / аудио-плеер, тап по фону закрывает */
export function MediaViewer({ file, onClose }: { file: File | null; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) { setUrl(null); return; }
    const u = URL.createObjectURL(file); setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  if (!file || !url) return null;
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: "calc(var(--z-modal) + 70)", background: "var(--black-a60)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}>
      {file.type.startsWith("image/") ? <img src={url} onClick={stop} style={{ maxWidth: "100%", maxHeight: "88dvh", objectFit: "contain" }} alt="" />
        : file.type.startsWith("video/") ? <video src={url} controls autoPlay playsInline onClick={stop} style={{ maxWidth: "100%", maxHeight: "88dvh" }} />
        : <audio src={url} controls autoPlay onClick={stop} style={{ width: "min(320px, 90vw)" }} />}
    </div>
  );
}
export const PLAYER_TAGS = ["18+", "конкурент", "идеал", "Д", "учитель", "Мама"];
export interface Candidate { name?: string | null; username?: string | null; first_name?: string | null; telegram_id?: number }
export interface DayOp { id: string; name: string }

export function UnifansSection({ variant, toName, dayOps, onDelegate, ach, players, onPlayers, candidates }: {
  variant: "own" | "given" | "delegate";
  toName?: string; dayOps: DayOp[]; onDelegate: (id: string | null) => void;
  ach: { items: AchItem[]; sel: Set<string>; help: Set<string>; open: boolean; onOpen: () => void; onSel: (id: string) => void; onHelp: (id: string) => void };
  players: RPlayer[]; onPlayers: (p: RPlayer[]) => void; candidates: Candidate[];
}) {
  const [delOpen, setDelOpen] = useState(false);
  /* закрытие списка операторов тапом мимо */
  useEffect(() => {
    if (!delOpen) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest && (t.closest(`.${css.cyberPop}`) || t.closest(`.${m.ufdel}`))) return;
      setDelOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [delOpen]);
  return (
    <section className={`${m.hud} ${m.gold}`}>
      <div className={`${m.hh} ${m.ufh}`}>Юнифанс
        {variant === "own" ? (
          <button type="button" className={m.ufdel} onClick={() => setDelOpen((o) => !o)} title="Запросить заполнение у оператора на филиале">→ передать</button>
        ) : null}
      </div>
      {delOpen && variant === "own" ? (
        <div className={`${css.cyberPop} ${css.popDown} ${css.open}`} style={{ position: "static", marginBottom: 6 }}>
          {dayOps.length === 0 ? <div className={css.empEmpty}>Нет других операторов на филиале в этот день</div> : null}
          {dayOps.map((o) => <button key={o.id} type="button" className={css.popItem} onClick={() => { setDelOpen(false); onDelegate(o.id); }}>{o.name}</button>)}
        </div>
      ) : null}
      {variant === "given" ? (
        <div className={m.ufbar}>заполняет {toName || "коллега"} · <b onClick={() => onDelegate(null)}>вернуть</b></div>
      ) : (<>
        {variant === "delegate" ? <div className={m.ufbar} style={{ marginBottom: 6 }}>запросил основной оператор</div> : null}
        <AchPicker items={ach.items} sel={ach.sel} help={ach.help} open={ach.open} onOpen={ach.onOpen} onSel={ach.onSel} onHelp={ach.onHelp} />
        {/* 28.08 (владелец): ручные карточки игроков убраны — привязавшиеся через
            юнифанс появляются сами, оператор только выставляет теги */}
        <div style={{ marginTop: 8 }}>
          {players.length === 0 ? <div className={m.ufbar}>Через юнифанс пока никто не привязался</div> : null}
          {players.map((p, i) => (
            <div key={i} className={m.ufpRow}>
              <span className={m.ufpNm}>✈ {p.name}{p.telegram && !/^\d+$/.test(p.telegram) ? <i className={m.ufpTg}> @{p.telegram}</i> : null}</span>
              <span className={m.ufpTags}>
                {/* 19.09 (владелец): «капитана пусть назначает оператор в отчёте с помощью тегов» —
                    капитанский QR убран, звезда одна на игру (ОО видит её в сводке дня, ДК — в досье) */}
                <button type="button" className={`${m.tag} ${p.captain ? m.on : ""}`} title="Капитан команды"
                  onClick={() => onPlayers(players.map((x, j) => ({ ...x, captain: j === i ? !x.captain : false })))}>
                  ★ капитан
                </button>
                {PLAYER_TAGS.map((t) => (
                  <button key={t} type="button" className={`${m.tag} ${p.tags.includes(t) ? m.on : ""}`}
                    onClick={() => onPlayers(players.map((x, j) => j === i ? { ...x, tags: x.tags.includes(t) ? x.tags.filter((y) => y !== t) : [...x.tags, t] } : x))}>
                    {t}
                  </button>
                ))}
              </span>
            </div>
          ))}
        </div>
        {/* «Медиа для клиента» переехало в отдельную секцию MediaSection (reportMedia.tsx, 23.08):
            грузится сразу, не ждёт отчёта и доступно любому оператору игры */}
      </>)}
    </section>
  );
}

/** Табличка «данные с вейверов» в Game review (26.08, владелец): ФИО (TextPad),
 *  дата рождения (BirthPicker), телефон (PhonePad) + удаление строки. */
export function WaiverPersons({ bookingId, persons, onPersons, candidates = [], saveState = { kind: "idle" } }: { bookingId: number | string; persons: WPerson[]; onPersons: (p: WPerson[]) => void; candidates?: Candidate[]; saveState?: WaiverSaveState }) {
  const [fioFor, setFioFor] = useState<number | null>(null);
  const [brFor, setBrFor] = useState<number | null>(null);
  const [phFor, setPhFor] = useState<number | null>(null);
  const [phAnchor, setPhAnchor] = useState<HTMLElement | null>(null);
  const [tgFor, setTgFor] = useState<number | null>(null);
  /* 13.09 (Влад: «добавил игроков с телефонами, а игр в Юнифансе у них нет»): по введённому
     номеру сразу спрашиваем сервер, есть ли такой аккаунт. Ключ — цифры номера, чтобы не
     гоняться за индексами строк. */
  const [ufMap, setUfMap] = useState<Record<string, string | null>>({});
  const digits = (v: string) => v.replace(/\D/g, "");
  const lookup = (phone: string) => {
    const k = digits(phone); if (k.length < 10) return;
    /* 13.10: сервер ищет номер только в рамках своей игры (booking-guard.ts) — без брони это был бы справочник по номерам */
    void api<{ found: boolean; name: string | null }>(`/api/game-reports/unifans-lookup?phone=${encodeURIComponent(phone)}&booking_id=${encodeURIComponent(String(bookingId))}`)
      .then((r) => setUfMap((prev) => ({ ...prev, [k]: r.found ? (r.name || "аккаунт") : null }))).catch(() => {});
  };
  const ufOf = (p: WPerson) => { const k = digits(p.phone); return k in ufMap ? ufMap[k] : p.uf; };
  useEffect(() => { /* закрытие тг-списка тапом мимо */
    if (tgFor == null) return;
    const onDown = (e: PointerEvent) => { const t = e.target as HTMLElement | null; if (!t?.closest?.(`.${css.cyberPop}`)) setTgFor(null); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [tgFor]);
  const upd = (i: number, patch: Partial<WPerson>) => onPersons(persons.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const tgKey = (c: Candidate) => String(c.username || c.telegram_id || "");
  const tgFace = (c: Candidate) => `${c.first_name || c.name || ""} ${c.username ? `@${c.username}` : ""}`.trim() || tgKey(c);
  return (<>
    <div className={m.lbl}>Данные с вейверов{saveState.kind === "saving" ? <span className={m.wpSave}> · сохраняем…</span> : saveState.kind === "saved" ? <span className={m.wpSave}> · сохранено ✓</span> : saveState.kind === "error" ? <span className={`${m.wpSave} ${m.wpSaveErr}`}> · {saveState.msg ? `не сохранилось: ${saveState.msg}` : "не сохранилось, повторим"}</span> : null}</div>
    {persons.map((p, i) => (
      <div key={p.lid ?? i} className={m.wpRow} style={{ position: "relative" }}>
        <button type="button" className={`${m.wpFio} ${p.fio ? "" : m.add}`} onClick={() => setFioFor(i)}>{p.fio || "+ ФИО"}</button>
        <button type="button" className={`${m.brin} ${p.birth_date ? m.bron : ""}`} onClick={() => setBrFor(i)}>
          {p.birth_date ? `🎂 ${p.birth_date.split("-").reverse().join(".")}` : "__.__.____"}
        </button>
        <button type="button" className={`${m.tag} ${m.telr} ${p.phone ? m.on : m.add}`} onClick={(e) => { setPhAnchor(e.currentTarget); setPhFor(i); }}>
          {p.phone ? `📞 ${p.phone}` : "+ телефон"}
        </button>
        {/* 28.08 (владелец): к вейвер-данным можно привязать юнифанс-аккаунт игры */}
        <button type="button" className={`${m.tag} ${p.telegram ? m.on : m.add}`} title="Юнифанс-аккаунт"
          style={{ maxWidth: 92, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          onClick={() => setTgFor((v) => (v === i ? null : i))}>
          ✈{p.telegram && !/^\d+$/.test(p.telegram) ? ` @${p.telegram}` : p.telegram ? ` ${p.telegram}` : ""}
        </button>
        {/* 13.09: сразу видно, появится ли игра у человека — аккаунт есть / номера в Юнифансе нет */}
        {!p.telegram && p.phone && ufOf(p) ? (
          <span className={`${m.tag} ${m.on}`} title="Аккаунт Юнифанса найден по телефону — игра и тайминги появятся у него, как только строка сохранится">✈ {ufOf(p)}</span>
        ) : !p.telegram && p.phone && ufOf(p) === null ? (
          <span className={m.tag} style={{ color: "var(--red)" }} title="Аккаунта с таким номером в Юнифансе нет: пусть откроет приложение и поделится номером — привяжем сами в течение 20 минут">не в Юнифансе</span>
        ) : null}
        <button type="button" className={m.plDel} title="Удалить строку" onClick={() => onPersons(persons.filter((_, j) => j !== i))}>✕</button>
        {tgFor === i ? (
          <div className={`${css.cyberPop} ${css.popDown} ${css.open} ${m.fitPop}`}>
            {candidates.map((c) => (
              <button key={tgKey(c)} type="button" className={css.popItem} onClick={() => { upd(i, { telegram: tgKey(c) }); setTgFor(null); }}>✈ {tgFace(c)}</button>
            ))}
            {p.telegram ? <button type="button" className={css.popItem} onClick={() => { upd(i, { telegram: "" }); setTgFor(null); }}><span style={{ color: "var(--red)" }}>✕ </span>отвязать</button> : null}
            {candidates.length === 0 && !p.telegram ? <div className={css.empEmpty}>К игре в юнифансе никто не прикрепился. По телефону привяжем сами, если номер есть в приложении</div> : null}
          </div>
        ) : null}
      </div>
    ))}
    <button type="button" className={`${m.pl} ${m.add2}`} onClick={() => onPersons([...persons, { fio: "", birth_date: "", phone: "" }])}>+ игрок с вейвера</button>
    {/* 18.09 (владелец): пад открывался пустым — опечатку в ФИО нельзя было поправить, только набрать заново */}
    <TextPad open={fioFor != null} label="ФИО игрока (как в вейвере)" placeholder="Фамилия Имя Отчество…" maxLength={160}
      value={fioFor != null ? persons[fioFor]?.fio ?? "" : ""}
      onCommit={(v) => { if (fioFor != null && v.trim()) upd(fioFor, { fio: v.trim() }); }} onClose={() => setFioFor(null)} />
    <BirthPicker open={brFor != null} value={brFor != null ? persons[brFor]?.birth_date ?? "" : ""}
      onCommit={(iso) => { if (brFor != null) upd(brFor, { birth_date: iso }); setBrFor(null); }} onCancel={() => setBrFor(null)} />
    <PhonePad open={phFor != null && !!phAnchor} anchor={phAnchor} label="Телефон игрока" value={phFor != null ? persons[phFor]?.phone ?? "" : ""}
      onCommit={(v) => { if (phFor != null) { upd(phFor, { phone: v, uf: undefined }); lookup(v); } }} onClose={() => { setPhFor(null); setPhAnchor(null); }} />
  </>);
}
